// File: src/components/UserManagement.js
// User management component

import React, { useState, useEffect } from 'react';
import { Trash2, Shield, Edit2, Key } from 'lucide-react';
import { API_BASE_URL } from '../App';
import { useLanguage } from '../contexts/LanguageContext';
import { useCountry } from '../contexts/CountryContext';
import { formatDateTimeForDisplay } from '../utils/utils';
import { handleUnauthorized } from '../utils/session';
import { useRoleLabel, assignableRoles, roleBadgeClass, normalizeRole } from '../utils/roles';
import usePermissions from '../hooks/usePermissions';
import CountryOptions from './CountryOptions';
import { getCountry } from '../data/countries';
import { useCountryName } from '../utils/countryNames';

function UserManagement({ token, userRole, onBack }) {
  const countryName = useCountryName();
  const { t } = useLanguage();
  const roleLabel = useRoleLabel();
  const permissions = usePermissions(userRole);
  const { currentCountry } = useCountry();

  // A country admin has no country to choose: the server puts every account
  // they create in their own. Only a super admin sees the picker.
  const picksCountry = permissions.isSuperAdmin();
  const roleOptions = assignableRoles(permissions);

  // Whose account this administrator may act on. The server decides the same
  // thing again; this only keeps the buttons honest.
  const manageable = (user) =>
    permissions.isSuperAdmin() || normalizeRole(user.role) !== 'super_admin';

  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    username: '', password: '', email: '', role: 'editor', country: currentCountry.code
  });
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (handleUnauthorized(response)) return;
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const addUser = async () => {
    if (!newUser.username || !newUser.password) {
      alert(t('usernameAndPasswordRequired'));
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newUser)
      });

      if (handleUnauthorized(response)) return;

      const data = await response.json();
      if (response.ok) {
        alert(t('userCreatedSuccessfully'));
        setNewUser({
          username: '', password: '', email: '', role: 'editor', country: currentCountry.code
        });
        loadUsers();
      } else {
        alert(data.error || t('failedToCreateUser'));
      }
    } catch (error) {
      alert(t('connectionError'));
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm(t('deleteThisUser'))) return;

    try {
      const response = await fetch(`${API_BASE_URL}/users/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (handleUnauthorized(response)) return;

      if (response.ok) {
        loadUsers();
      } else {
        const data = await response.json();
        alert(data.error || t('failedToDeleteUser'));
      }
    } catch (error) {
      alert(t('connectionError'));
    }
  };

  const updateUser = async () => {
    if (!editingUser.username) {
      alert(t('usernameRequired'));
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username: editingUser.username,
          email: editingUser.email,
          role: editingUser.role,
          country: editingUser.country
        })
      });

      if (handleUnauthorized(response)) return;

      const data = await response.json();
      if (response.ok) {
        alert(t('userUpdatedSuccessfully'));
        setEditingUser(null);
        loadUsers();
      } else {
        alert(data.error || t('failedToUpdateUser'));
      }
    } catch (error) {
      alert(t('connectionError'));
    }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert(t('passwordTooShort'));
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/users/${resetPasswordUser.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ newPassword })
      });

      if (handleUnauthorized(response)) return;

      const data = await response.json();
      if (response.ok) {
        alert(t('passwordResetSuccessfully'));
        setResetPasswordUser(null);
        setNewPassword('');
      } else {
        alert(data.error || t('failedToResetPassword'));
      }
    } catch (error) {
      alert(t('connectionError'));
    }
  };

  return (
    <div className="app-shell p-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-ink">{t('userManagement')}</h1>
          <button
            onClick={onBack}
            className="bg-paper-alt hover:opacity-80 text-ink px-4 py-2 rounded-lg font-semibold"
          >
            {t('backToAdmin')}
          </button>
        </div>

        <div className="bg-paper rounded-lg p-6 border border-line mb-6">
          <h2 className="text-xl font-bold text-ink mb-4">{t('addNewUser')}</h2>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              placeholder={t('username')}
              value={newUser.username}
              onChange={(e) => setNewUser({...newUser, username: e.target.value})}
              className="px-4 py-2 bg-paper-alt border border-line rounded text-ink"
            />
            <input
              type="password"
              placeholder={t('password')}
              value={newUser.password}
              onChange={(e) => setNewUser({...newUser, password: e.target.value})}
              className="px-4 py-2 bg-paper-alt border border-line rounded text-ink"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="email"
              placeholder={t('emailOptional')}
              value={newUser.email}
              onChange={(e) => setNewUser({...newUser, email: e.target.value})}
              className="px-4 py-2 bg-paper-alt border border-line rounded text-ink"
            />
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({...newUser, role: e.target.value})}
              className="px-4 py-2 bg-paper-alt border border-line rounded text-ink"
              aria-label={t('role')}
            >
              {roleOptions.map(role => (
                <option key={role} value={role}>{roleLabel(role)}</option>
              ))}
            </select>
          </div>
          {picksCountry && (
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <select
                value={newUser.country}
                onChange={(e) => setNewUser({...newUser, country: e.target.value})}
                className="px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                aria-label={t('country')}
              >
                <CountryOptions />
              </select>
            </div>
          )}
          <div className="mt-3 p-3 bg-paper-alt rounded text-sm text-ink-muted">
            <p className="font-semibold mb-1">{t('rolePermissions')}</p>
            {permissions.isSuperAdmin() && (
              <p>
                <span className="text-yellow-700 dark:text-yellow-400">{t('superAdminRole')}</span>
                {' '}{t('superAdminDescription')}
              </p>
            )}
            <p>
              <span className="text-orange-700 dark:text-orange-400">{t('countryAdminRole')}</span>
              {' '}{t('countryAdminDescription')}
            </p>
            <p><span className="text-blue-700 dark:text-blue-400">{t('editorRole')}</span> {t('editorDescription')}</p>
            <p><span className="text-green-700 dark:text-green-400">{t('organizerRole')}</span> {t('organizerDescription')}</p>
            <p><span className="text-ink-muted">{t('competitorRole')}</span> {t('competitorDescription')}</p>
            {!picksCountry && <p className="mt-2">{t('countryAdminScopeHint')}</p>}
          </div>
          <button
            onClick={addUser}
            className="mt-4 bg-accent hover:opacity-90 text-accent-ink px-6 py-2 rounded font-semibold"
          >
            {t('addUser')}
          </button>
        </div>

        <div className="bg-paper rounded-lg p-6 border border-line">
          <h2 className="text-xl font-bold text-ink mb-4">{t('existingUsers')}</h2>
          {loading ? (
            <p className="text-ink-muted">{t('loading')}</p>
          ) : (
            <div className="space-y-3">
              {users.map(user => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 bg-paper-alt rounded"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="text-ink font-medium">{user.username}</div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded flex items-center gap-1 ${roleBadgeClass(user.role)}`}>
                        <Shield className="w-3 h-3" />
                        {roleLabel(user.role)}
                      </span>
                      <span className="text-xs text-ink-muted">
                        {countryName(getCountry(user.country))}
                      </span>
                    </div>
                    {user.email && <div className="text-ink-muted text-sm">{user.email}</div>}
                    <div className="text-ink-muted text-xs">
                      {t('created')} {formatDateTimeForDisplay(user.created_at)}
                      {user.last_login && ` • ${t('lastLogin')} ${formatDateTimeForDisplay(user.last_login)}`}
                    </div>
                  </div>
                  {/* A country admin may not touch a super admin - the server
                      refuses it - so the buttons that would 403 are not shown.
                      They can still SEE them: knowing who to ask is useful. */}
                  <div className="flex items-center gap-2">
                    {!manageable(user) ? (
                      <span className="text-ink-muted text-xs">{t('managedBySuperAdmin')}</span>
                    ) : (
                    <>
                    <button
                      onClick={() => setEditingUser({...user})}
                      className="text-blue-700 dark:text-blue-400 hover:opacity-80"
                      title={t('edit')}
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setResetPasswordUser(user)}
                      className="text-yellow-700 dark:text-yellow-400 hover:opacity-80"
                      title={t('resetPassword')}
                    >
                      <Key className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => deleteUser(user.id)}
                      className="text-red-700 dark:text-red-400 hover:opacity-80"
                      title={t('delete')}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                    </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Edit User Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-paper rounded-lg p-6 max-w-md w-full border border-line">
              <h3 className="text-xl font-bold text-ink mb-4">{t('editUser')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-ink-muted mb-2">{t('username')}</label>
                  <input
                    type="text"
                    value={editingUser.username}
                    onChange={(e) => setEditingUser({...editingUser, username: e.target.value})}
                    className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                  />
                </div>
                <div>
                  <label className="block text-ink-muted mb-2">{t('email')}</label>
                  <input
                    type="email"
                    value={editingUser.email || ''}
                    onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                    className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                  />
                </div>
                <div>
                  <label className="block text-ink-muted mb-2">{t('role')}</label>
                  <select
                    value={editingUser.role}
                    onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                    className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                  >
                    {roleOptions.map(role => (
                      <option key={role} value={role}>{roleLabel(role)}</option>
                    ))}
                  </select>
                </div>
                {picksCountry && (
                  <div>
                    <label className="block text-ink-muted mb-2">{t('country')}</label>
                    <select
                      value={editingUser.country || currentCountry.code}
                      onChange={(e) => setEditingUser({...editingUser, country: e.target.value})}
                      className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                    >
                      <CountryOptions />
                    </select>
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={updateUser}
                  className="flex-1 bg-accent hover:opacity-90 text-accent-ink py-2 rounded font-semibold"
                >
                  {t('save')}
                </button>
                <button
                  onClick={() => setEditingUser(null)}
                  className="flex-1 bg-paper-alt hover:opacity-80 text-ink py-2 rounded font-semibold"
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {resetPasswordUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-paper rounded-lg p-6 max-w-md w-full border border-line">
              <h3 className="text-xl font-bold text-ink mb-4">{t('resetPassword')}</h3>
              <p className="text-ink-muted mb-4">
                {t('resetPasswordFor')}: <strong className="text-ink">{resetPasswordUser.username}</strong>
              </p>
              <div className="mb-4">
                <label className="block text-ink-muted mb-2">{t('newPassword')}</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('minimumSixCharacters')}
                  className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={resetPassword}
                  className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white py-2 rounded font-semibold"
                >
                  {t('resetPassword')}
                </button>
                <button
                  onClick={() => {
                    setResetPasswordUser(null);
                    setNewPassword('');
                  }}
                  className="flex-1 bg-paper-alt hover:opacity-80 text-ink py-2 rounded font-semibold"
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserManagement;